import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const authAbortControllerRef = useRef(null);

    const checkAuth = useCallback(async () => {
        console.log('[AUTH DEBUG] checkAuth called');
        
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes('session_id=')) {
            console.log('[AUTH DEBUG] OAuth callback detected, skipping');
            setLoading(false);
            return;
        }
        
        const storedToken = localStorage.getItem('auth_token');
        console.log('[AUTH DEBUG] Stored token present:', !!storedToken);
        
        if (!storedToken) {
            console.log('[AUTH DEBUG] No token found, clearing auth state');
            setUser(null);
            setLoading(false);
            return;
        }
        
        // Cancel any previous auth check
        if (authAbortControllerRef.current) {
            authAbortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        authAbortControllerRef.current = abortController;
        
        // Retry logic for transient failures (server restarts cause 502/503)
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const response = await fetch(`${API_URL}/auth/me`, {
                    credentials: 'include',
                    headers: { 'Authorization': `Bearer ${storedToken}` },
                    signal: abortController.signal,
                });
                
                // Check if aborted before processing
                if (abortController.signal.aborted) return;
                
                console.log('[AUTH DEBUG] /auth/me response status:', response.status);
                
                if (response.ok) {
                    const userData = await response.json();
                    if (abortController.signal.aborted) return;
                    console.log('[AUTH DEBUG] User data received:', userData?.email);
                    setUser(userData);
                    setLoading(false);
                    return;
                }
                
                // Definitive auth rejection — token is invalid/expired
                if (response.status === 401 || response.status === 403) {
                    console.log('[AUTH DEBUG] Token rejected (', response.status, '), clearing auth');
                    localStorage.removeItem('auth_token');
                    setUser(null);
                    setLoading(false);
                    return;
                }
                
                // Transient server error — retry
                if ((response.status === 502 || response.status === 503 || response.status >= 500) && attempt < maxRetries) {
                    console.log(`[AUTH DEBUG] Transient ${response.status} on attempt ${attempt}, retrying...`);
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                    continue;
                }
                
                // Final attempt still failed with server error — keep existing state if we have a token
                console.log('[AUTH DEBUG] /auth/me failed with', response.status, '- preserving session');
                setLoading(false);
                return;
                
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('[AUTH DEBUG] Auth check aborted');
                    return;
                }
                
                // Network error — retry
                if (attempt < maxRetries) {
                    console.log(`[AUTH DEBUG] Network error on attempt ${attempt}, retrying...`);
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                    continue;
                }
                
                // All retries exhausted — preserve session if token exists
                console.error('[AUTH DEBUG] Auth check failed after retries:', error);
                setLoading(false);
                return;
            }
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password, captchaToken = null) => {
        const body = { email, password };
        if (captchaToken) {
            body.captcha_token = captchaToken;
        }
        
        console.log('[AUTH] Attempting login to:', `${API_URL}/auth/login`);
        
        // Retry logic for transient network issues (e.g., CORS preflight cold start)
        const maxRetries = 2;
        let lastError = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            let response;
            try {
                response = await fetch(`${API_URL}/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(body),
                });
            } catch (fetchError) {
                // Pure network error (Failed to fetch) - retry if possible
                const isNetworkError = fetchError.name === 'TypeError' && 
                    fetchError.message === 'Failed to fetch';
                
                if (isNetworkError && attempt < maxRetries) {
                    console.log(`[AUTH] Network error on attempt ${attempt}, retrying...`);
                    lastError = fetchError;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                }
                
                // All retries exhausted or non-retryable error
                lastError = fetchError;
                break;
            }
            
            // Retry on transient proxy errors (502/503 during server restarts)
            if ((response.status === 502 || response.status === 503) && attempt < maxRetries) {
                console.log(`[AUTH] Transient ${response.status} on attempt ${attempt}, retrying...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
            }
            
            // We got a response - handle it based on status code
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = { detail: 'Login failed. Please try again.' };
                }
                
                const errorCode = response.status;
                console.log('[AUTH] Login error response:', errorCode, errorData);
                
                // Check for captcha requirement first
                if (response.headers.get('X-Require-Captcha') === 'true' || 
                    errorData.detail?.includes('security check')) {
                    const captchaError = new Error(errorData.detail || 'Security check required');
                    captchaError.requireCaptcha = true;
                    throw captchaError;
                }
                
                // Create specific error based on status code - throw immediately, no retry
                let errorMessage;
                if (errorCode === 401) {
                    errorMessage = 'Incorrect email or password. Please try again.';
                } else if (errorCode === 404) {
                    errorMessage = 'Account not found. Please check your email or create an account.';
                } else if (errorCode === 400) {
                    errorMessage = errorData.detail || 'Please check your input and try again.';
                } else if (errorCode === 429) {
                    errorMessage = 'Too many attempts. Please wait a moment and try again.';
                } else if (errorCode === 502 || errorCode === 503) {
                    errorMessage = 'Server is momentarily restarting. Please try again in a few seconds.';
                } else if (errorCode >= 500) {
                    errorMessage = errorData.detail || 'An unexpected server error occurred. Please try again.';
                } else {
                    errorMessage = errorData.detail || 'Login failed. Please try again.';
                }
                
                // Throw auth error directly - no need for custom property checking
                throw new Error(errorMessage);
            }
            
            // Success - process the response
            const data = await response.json();
    
            if (data.token) {
                try {
                    localStorage.setItem('auth_token', data.token);
                } catch (e) {
                    console.error('localStorage.setItem failed:', e);
                }
            }
            
            setUser(data.user);
            return data;
        }
        
        // All retries exhausted - only reached for actual network failures
        console.error('[AUTH] Login failed after retries:', lastError);
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
    };

    const register = async (email, password, name, role = 'buyer', captchaToken = null) => {
        const body = { email, password, name, role };
        if (captchaToken) {
            body.captcha_token = captchaToken;
        }
        
        const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Registration failed');
        }
        
        const data = await response.json();
        if (data.token) {
            try {
                localStorage.setItem('auth_token', data.token);
            } catch (e) {
                console.error('localStorage.setItem failed:', e);
            }
        }
        setUser(data.user);
        return data;
    };

    const logout = async () => {
        // Cancel any pending auth requests to prevent race conditions
        if (authAbortControllerRef.current) {
            authAbortControllerRef.current.abort();
            authAbortControllerRef.current = null;
        }
        
        try {
            await fetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        // Clear all auth-related storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('cart_session_id'); // Clear cart session on logout
        sessionStorage.clear(); // Clear any session-specific state
        setUser(null);
    };

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleOAuthCallback = async (sessionId) => {
        const response = await fetch(`${API_URL}/auth/session?session_id=${sessionId}`, {
            credentials: 'include',
        });
        
        if (!response.ok) {
            throw new Error('OAuth session exchange failed');
        }
        
        const data = await response.json();
        
        // Store the JWT token (same as email/password login)
        if (data.token) {
            localStorage.setItem('auth_token', data.token);
        }
        
        setUser(data.user);
        return data;
    };

    // Force refresh auth state and return the updated user
    // This is useful after actions that change user state (like seller registration)
    const refreshAuth = async () => {
        console.log('[AUTH DEBUG] refreshAuth called - forcing user state refresh');
        
        const headers = {};
        const storedToken = localStorage.getItem('auth_token');
        
        if (!storedToken) {
            console.log('[AUTH DEBUG] No token, cannot refresh');
            return null;
        }
        
        headers['Authorization'] = `Bearer ${storedToken}`;
        
        try {
            const response = await fetch(`${API_URL}/auth/me`, {
                credentials: 'include',
                headers,
            });
            
            if (response.ok) {
                const userData = await response.json();
                console.log('[AUTH DEBUG] refreshAuth got user:', userData?.email, 'role:', userData?.role);
                setUser(userData);
                return userData;
            } else {
                console.log('[AUTH DEBUG] refreshAuth failed:', response.status);
                return null;
            }
        } catch (error) {
            console.error('[AUTH DEBUG] refreshAuth error:', error);
            return null;
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        loginWithGoogle,
        handleOAuthCallback,
        checkAuth,
        refreshAuth,
        isAuthenticated: !!user,
        isSeller: user?.role === 'seller' || user?.role === 'admin',
        isAdmin: user?.role === 'admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
